import {gql} from '@apollo/client';
import React, {useState} from 'react';

import {StorableSummary} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  doesCurrentUserHaveAccessForItem,
  isItemAvailableToCurrentUser,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import SampleStatusTable from 'inventory/sample-status/SampleStatusTable';
import SampleStatusWarningBannerStack from 'inventory/sample-status/SampleStatusWarningBannerStack';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import APIUtil from 'util/APIUtil';

import {
  SampleStatusWarningQuery,
  SampleStatusWarningQueryVariables,
} from './__generated/SampleStatusWarningQuery';

type SampleStatusStorable = SampleStatusWarningQuery['storables'][number];

export interface SampleStatusWarningProps {
  sampleIds: Array<string>;
  samples: Array<SampleStatusItem>;
  selectedSampleIds: Array<string>;
  setSelectedSampleIds: (newSelectedSampleIds: Array<string>) => void;
  shouldProceed: boolean;
  setShouldProceed: (newShouldProceed: boolean) => void;
  trackingParams: BaseSampleStatusWarningTrackingParams;
  canSelectMultiple?: boolean;
}

export const SAMPLE_STATUS_WARNING_QUERY = gql`
  query SampleStatusWarningQuery($sampleIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $sampleIds) {
      ... on StorableInterface {
        id
        sortKey: name
      }
      ... on ContainerNode {
        id
        checkOutStatusInfo: checkOutStatusInfoJson
        sampleRoles {
          ...SampleRolesGraphQL
        }
        restrictionStatus
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('SampleRoles').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface CheckForWarningsResult {
  shouldShowWarning: boolean;
  samples?: Array<SampleStatusItem>;
}

function checkForWarnings(
  samples: Array<SampleStatusItem>,
  sampleIds: Array<string>,
  showOnlySamplesWithWarnings: boolean = false,
  shouldAlwaysShowWarning: boolean = false
): CheckForWarningsResult {
  const samplesWithWarnings = samples.filter(
    (sample) => !isItemAvailableToCurrentUser(sample) || !doesCurrentUserHaveAccessForItem(sample)
  );

  if (shouldAlwaysShowWarning || samplesWithWarnings.length > 0) {
    // eslint-disable-next-line benchlint/no-spread-reduce
    const samplesById = samples.reduce(
      (byId: {[sampleId: string]: SampleStatusItem}, sample) => ({...byId, [sample.id]: sample}),
      {}
    );

    const filteredSampleIds = showOnlySamplesWithWarnings
      ? samplesWithWarnings.map((sample) => sample.id)
      : sampleIds;
    const allSamples = filteredSampleIds.map((sampleId) => samplesById[sampleId] ?? {id: sampleId});
    return {
      shouldShowWarning: true,
      samples: allSamples,
    };
  } else {
    return {
      shouldShowWarning: false,
    };
  }
}

export function checkSamplesForStatusWarnings({
  samples,
  showOnlySamplesWithWarnings = false,
}: {
  samples: Array<StorableSummary>;
  showOnlySamplesWithWarnings?: boolean;
}): CheckForWarningsResult {
  if (!hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)) {
    return {
      shouldShowWarning: false,
    };
  }
  const sampleStatusItems: Array<SampleStatusItem> = samples.map(
    ({id, name, checkOutStatusInfo, sampleRoles, restrictionStatus}) => ({
      id,
      sortKey: name,
      checkOutStatusInfo,
      sampleRoles,
      restrictionStatus,
    })
  );
  return checkForWarnings(
    sampleStatusItems,
    samples.map(({id}) => id),
    showOnlySamplesWithWarnings
  );
}

export async function checkForSampleStatusWarnings({
  sampleIds,
  showOnlySamplesWithWarnings = false,
  shouldAlwaysShowWarning = false,
}: {
  sampleIds: Array<string>;
  showOnlySamplesWithWarnings?: boolean;
  shouldAlwaysShowWarning?: boolean;
}): Promise<CheckForWarningsResult> {
  const storableIds = sampleIds.filter((id) => APIUtil.isStorable(id));
  if (!(shouldAlwaysShowWarning || hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1))) {
    return {
      shouldShowWarning: false,
    };
  }
  const result = await doQuerySafe<SampleStatusWarningQuery, SampleStatusWarningQueryVariables>(
    {query: SAMPLE_STATUS_WARNING_QUERY},
    {
      variables: {sampleIds: storableIds},
    }
  );

  const samples: Array<SampleStatusItem> = result.storables.flatMap((s: SampleStatusStorable) =>
    isInaccessible(s) ? [] : [s]
  );
  return checkForWarnings(samples, sampleIds, showOnlySamplesWithWarnings, shouldAlwaysShowWarning);
}

export function canProceedWithSamples(
  selectedSampleIds: Array<string>,
  shouldShowWarning: boolean,
  canProceedOnSampleStatus: boolean
): boolean {
  return !shouldShowWarning || (canProceedOnSampleStatus && selectedSampleIds.length > 0);
}

export function SampleStatusWarning(props: SampleStatusWarningProps): JSX.Element {
  const [hasOverridableWarnings, setHasOverridableWarnings] = useState(true);

  return (
    <>
      <SampleStatusWarningBannerStack
        canDeselectSamples={props.canSelectMultiple ?? true}
        shouldProceed={props.shouldProceed}
        onChangeShouldProceed={props.setShouldProceed}
        onChangeHasOverridableWarnings={setHasOverridableWarnings}
        hasOverridableWarnings={hasOverridableWarnings}
        trackingParamsWithSampleIds={{...props.trackingParams, sampleIds: props.sampleIds}}
        samples={props.samples}
        selectedSampleIds={props.selectedSampleIds}
        setSelectedSampleIds={props.setSelectedSampleIds}
      />
      <SampleStatusTable
        samples={props.samples}
        allowSelection={!hasOverridableWarnings}
        selectedSampleIds={props.selectedSampleIds}
        setSelectedSampleIds={props.setSelectedSampleIds}
        canSelectMultiple={props.canSelectMultiple}
      />
    </>
  );
}

interface ConditionallyRenderSampleStatusWarningArgs
  extends Omit<SampleStatusWarningProps, 'sampleIds' | 'samples'> {
  samples: Array<SampleStatusItem> | null;
  shouldShowStatusWarning: boolean;
}

export function conditionallyRenderSampleStatusWarning({
  shouldShowStatusWarning,
  samples,
  trackingParams,
  selectedSampleIds,
  setSelectedSampleIds,
  shouldProceed,
  setShouldProceed,
  canSelectMultiple,
}: ConditionallyRenderSampleStatusWarningArgs): JSX.Element | null {
  return shouldShowStatusWarning && samples != null ? (
    <SampleStatusWarning
      sampleIds={samples.map(({id}) => id)}
      selectedSampleIds={selectedSampleIds}
      setSelectedSampleIds={setSelectedSampleIds}
      shouldProceed={shouldProceed}
      setShouldProceed={setShouldProceed}
      trackingParams={trackingParams}
      samples={samples}
      canSelectMultiple={canSelectMultiple}
    />
  ) : null;
}
